/**
 * Scroll to a section by id.
 *
 * Replaces the old window.scrollTo(0, 3400) / 6610 pixel offsets, which were
 * wrong at most viewport widths and broke whenever content changed.
 *
 * Long scrolls need a correction pass. Lazy-loaded images below the fold
 * finish loading *while* a smooth scroll is in flight, which grows the page
 * under it, and the browser does not re-aim a scroll it has already started —
 * it just stops short. So after the scroll settles we re-measure and, if the
 * target drifted, go again.
 */
import { trackCta } from "./useAnalytics";
import { prefersReducedMotion } from "./useScrollReveal";

/** How far off target we tolerate before correcting. */
const DRIFT_TOLERANCE = 40;
const MAX_CORRECTIONS = 3;

function scrollPaddingTop() {
  const value = getComputedStyle(document.documentElement).scrollPaddingTop;
  return parseFloat(value) || 0;
}

/** Runs `done` once the page stops scrolling, or after a timeout. */
function onScrollSettled(done) {
  let timer = null;

  const finish = () => {
    window.removeEventListener("scrollend", finish);
    clearTimeout(timer);
    done();
  };

  // scrollend is not everywhere yet, so the timeout is the real guarantee.
  window.addEventListener("scrollend", finish);
  timer = setTimeout(finish, 1000);
}

function scrollToElement(el, attemptsLeft = MAX_CORRECTIONS) {
  const behavior = prefersReducedMotion() ? "auto" : "smooth";
  el.scrollIntoView({ behavior, block: "start" });

  if (attemptsLeft <= 0) return;

  onScrollSettled(() => {
    const drift = Math.abs(el.getBoundingClientRect().top - scrollPaddingTop());
    // Already at the very bottom of the page? Then this is as close as it gets.
    const atEnd =
      window.scrollY + window.innerHeight >=
      document.documentElement.scrollHeight - 2;

    if (drift > DRIFT_TOLERANCE && !atEnd) {
      scrollToElement(el, attemptsLeft - 1);
    }
  });
}

/**
 * @param {string} id element id, with or without the leading #
 * @param {string} [ctaLocation] fires cta_click with this location
 */
export function scrollToSection(id, ctaLocation) {
  if (ctaLocation) trackCta(ctaLocation);

  const el = document.getElementById(id.replace(/^#/, ""));
  if (!el) return;

  scrollToElement(el);
}

/** Scroll to the lead form and put the cursor in its first field. */
export function scrollToLeadForm(ctaLocation) {
  if (ctaLocation) trackCta(ctaLocation);

  const el = document.getElementById("lead-form");
  if (!el) return;

  scrollToElement(el);

  // preventScroll so focusing does not fight the scroll that is still running.
  onScrollSettled(() => {
    document.getElementById("lead-name")?.focus({ preventScroll: true });
  });
}

export function useScrollTo() {
  return { scrollToSection, scrollToLeadForm };
}
